/**
 * DoorDash API validation functions
 */

import type { ApiDoorDashQuoteParams } from './types/ApiDoorDash';
import type { ApiParamValidator, ApiParamValidatorMap } from '../utils/types';
import { validateAllParams } from '../utils';

/**
 * Error messages consistent with current DoorDash usage/tests
 */
enum DoorDashApiErrors {
    MISSING_TASK_ID = 'Task ID is required and cannot be empty',
    MISSING_QUOTE_PARAMS = 'Quote parameters are required',
    MISSING_PICKUP_PHONE_NUMBER = 'Pickup phone number is required',
    MISSING_QUOTE_ID = 'Quote ID is required and cannot be empty',
    MISSING_CARRIER_ID = 'Carrier ID is required and cannot be empty'
}

/**
 * Non-throwing validators for ZoneSetsApi-style param validation
 */
const validateNonEmptyString: ApiParamValidator<string> = (param) => {
    const isMissing =
        !param || (typeof param === 'string' && param.trim() === '');
    return { isMissing, isInvalid: false };
};

const validateQuoteParamsPresence: ApiParamValidator<ApiDoorDashQuoteParams> = (
    param
) => {
    const isMissing = !param;
    if (isMissing) return { isMissing: true, isInvalid: false };

    const isPickupPhoneMissing =
        !param.pickup_phone_number ||
        (typeof param.pickup_phone_number === 'string' &&
            param.pickup_phone_number.trim() === '');

    return { isMissing: false, isInvalid: isPickupPhoneMissing };
};

interface GenerateQuoteArgs {
    taskId: string;
    params: ApiDoorDashQuoteParams;
}
interface AcceptMultiCarrierQuoteArgs {
    taskId: string;
    quoteId: string;
    carrierId: string;
}

const generateQuoteValidation: ApiParamValidatorMap<GenerateQuoteArgs> = {
    taskId: {
        validator: validateNonEmptyString,
        missingMessage: DoorDashApiErrors.MISSING_TASK_ID
    },
    params: {
        validator: validateQuoteParamsPresence,
        missingMessage: DoorDashApiErrors.MISSING_QUOTE_PARAMS,
        invalidMessage: DoorDashApiErrors.MISSING_PICKUP_PHONE_NUMBER
    }
};

const acceptQuoteValidation: ApiParamValidatorMap<{ taskId: string }> = {
    taskId: {
        validator: validateNonEmptyString,
        missingMessage: DoorDashApiErrors.MISSING_TASK_ID
    }
};

const acceptMultiCarrierQuoteValidation: ApiParamValidatorMap<AcceptMultiCarrierQuoteArgs> =
    {
        taskId: {
            validator: validateNonEmptyString,
            missingMessage: DoorDashApiErrors.MISSING_TASK_ID
        },
        quoteId: {
            validator: validateNonEmptyString,
            missingMessage: DoorDashApiErrors.MISSING_QUOTE_ID
        },
        carrierId: {
            validator: validateNonEmptyString,
            missingMessage: DoorDashApiErrors.MISSING_CARRIER_ID
        }
    };

export const getGenerateQuoteParamErrors = (args: GenerateQuoteArgs) => {
    // validateAllParams constrains its generic to an index-signature record;
    // interfaces carry no implicit index signature, so cast at the call.
    return validateAllParams(
        args as unknown as Record<string, unknown>,
        generateQuoteValidation as ApiParamValidatorMap<Record<string, unknown>>
    );
};

export const getAcceptQuoteParamErrors = (args: { taskId: string }) => {
    return validateAllParams(args, acceptQuoteValidation);
};

export const getGenerateMultiCarrierQuoteParamErrors =
    getGenerateQuoteParamErrors;

export const getAcceptMultiCarrierQuoteParamErrors = (
    args: AcceptMultiCarrierQuoteArgs
) => {
    return validateAllParams(
        args as unknown as Record<string, unknown>,
        acceptMultiCarrierQuoteValidation as ApiParamValidatorMap<
            Record<string, unknown>
        >
    );
};
